import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ChartLineUp, GoogleLogo, ShieldCheck, Bank } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TopLinks } from "@/components/AppShell";
import { SiteFooter } from "@/components/SiteFooter";
import { Seo } from "@/components/Seo";

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function AuthForm({ mode }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await api.post(`/auth/${mode}`, mode === "login" ? { email: form.email, password: form.password } : form);
      setUser(r.data);
      navigate("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${mode}-form`}>
      {mode === "register" && (
        <div className="space-y-1.5">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" value={form.name} onChange={set("name")} required data-testid="register-name-input" />
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor={`${mode}-email`}>Email</Label>
        <Input id={`${mode}-email`} type="email" value={form.email} onChange={set("email")} required data-testid={`${mode}-email-input`} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${mode}-password`}>Password</Label>
        <Input id={`${mode}-password`} type="password" minLength={mode === "register" ? 8 : 1} value={form.password} onChange={set("password")} required data-testid={`${mode}-password-input`} />
      </div>
      {error && <p className="text-sm text-rose-600" data-testid={`${mode}-error`}>{error}</p>}
      <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid={`${mode}-submit-button`}>
        {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
      </Button>
    </form>
  );
}

const POINTS = [
  { icon: ChartLineUp, t: "Official TSP share prices, logged nightly" },
  { icon: Bank, t: "TSP, Traditional and Roth IRAs in one ledger" },
  { icon: ShieldCheck, t: "Projections, what-if scenarios and RMD planning" },
];

export default function Login() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  if (user) return <Navigate to="/dashboard" replace />;
  return (
    <div className="relative min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="orb w-[420px] h-[420px] -top-20 -left-20" style={{ background: "var(--orb-a)" }} />
      <div className="orb w-[380px] h-[380px] bottom-0 right-1/3" style={{ background: "var(--orb-b)" }} />
      <div className="absolute top-6 right-6 z-10 flex items-center gap-4"><TopLinks /><ThemeToggle /></div>
      <Seo title="Sign in" description="Sign in or create a free account to track your TSP, Traditional and Roth IRAs with official daily fund prices and retirement projections." path="/login" />
      <section className="relative z-[1] hidden lg:flex flex-col justify-between p-14">
        <div className="flex items-center gap-2 font-display text-lg font-semibold" data-testid="brand-logo">
          <img src="/pillarwealth-icon.png" alt="PillarWealth logo" className="h-10 w-10 rounded-lg" />
          PillarWealth
        </div>
        <div className="max-w-xl fade-up">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-700 dark:text-cyan-400 mb-4">TSP · Traditional IRA · Roth IRA</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-grad-head">Know where your federal retirement is headed.</h1>
          <p className="mt-6 text-base text-slate-600 dark:text-slate-400 max-w-md">Track every G, F, C, S, I and L fund share, model the agency match, and see whether today's paycheck gets you to the number you need.</p>
          <ul className="mt-10 space-y-4">
            {POINTS.map(({ icon: I, t }, i) => (
              <li key={t} className="flex items-center gap-3 text-slate-700 dark:text-slate-300 fade-up" style={{ animationDelay: `${150 + i * 100}ms` }}>
                <I size={20} className="text-emerald-600 dark:text-emerald-400" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-slate-500">Projections are estimates, not financial advice.</p>
      </section>
      <section className="relative z-[1] flex flex-col items-center justify-center p-6 pt-20">
        <div className="glass w-full max-w-md p-8 fade-up" data-testid="auth-card">
          <h2 className="font-display text-2xl font-semibold text-slate-900 dark:text-white">Welcome</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Sign in to your retirement ledger.</p>
          <Button variant="outline" className="w-full rounded-full gap-2" onClick={googleLogin} data-testid="google-login-button">
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </Button>
          <div className="flex items-center gap-3 my-6 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />or with email<span className="h-px flex-1 bg-border" /></div>
          <Tabs defaultValue={params.get("tab") === "register" ? "register" : "login"}>
            <TabsList className="grid grid-cols-2 w-full mb-5">
              <TabsTrigger value="login" data-testid="login-tab">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Create account</TabsTrigger>
            </TabsList>
            <TabsContent value="login"><AuthForm mode="login" /></TabsContent>
            <TabsContent value="register"><AuthForm mode="register" /></TabsContent>
          </Tabs>
        </div>
        <div className="w-full max-w-md"><SiteFooter /></div>
      </section>
    </div>
  );
}
